import { useCallback, useEffect, useState } from 'react';
import {
  SOCKET_TRANSPORTS,
  VALUE_CHAIN_MAP_ANALYSIS_RESULT_PROCESSING_STATUSES,
  VALUE_CHAIN_MAP_ANALYSIS_RESULTS_POLLING_INTERVAL,
  VALUE_CHAIN_MAP_ANALYSIS_WS_EVENT_RESULT_UPDATED,
} from 'constants/general';
import { NOT_FOUND } from 'constants/statusCodes';
import useWebSocket from 'hooks/useWebSocket';
import { useAppDispatch } from 'store/hooks/useApp';
import {
  useGetValueChainMapAnalysisResultByIdQuery, valueChainMapApi,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { VALUE_CHAIN_MAP_ANALYSES_TAG } from 'store/tags/valueChainMap';

import type { IVCAnalysisResultUpdatedPayload } from 'hooks/interfaces/IUseWebSocket';
import type { IAiChatAnalysisResultRef, IAiChatAnalysisResultState } from '../interfaces/IAiChatAnalysisResult';

/** Whether a read failed because the run is gone. */
const isNotFound = (error: unknown): boolean => (
  typeof error === 'object' && error !== null && 'status' in error && error.status === NOT_FOUND
);

/**
 * One run of an impact analysis, kept current the way the map's panel keeps its own list.
 *
 * Read by id from the same cache the panel reads through. While the run is in progress, the run
 * is polled at the panel's interval and the `vc.analysis.result.updated` event is listened for;
 * either one moves the cache, and the panel's own listener moves the same entries, so the card
 * here and the card in the panel show one state whichever listener heard the event first. A
 * run that is gone — deleted in the panel or here — reads as deleted, not as a failure.
 */
const useAiChatAnalysisResult = ({
  valueChainMapId, analysisId, resultId,
}: IAiChatAnalysisResultRef): IAiChatAnalysisResultState => {
  const dispatch = useAppDispatch();
  // Known one render late, which is what the poll option needs: it is read on each render.
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const {
    data, isLoading, isError, error,
  } = useGetValueChainMapAnalysisResultByIdQuery(
    { valueChainMapId: `${valueChainMapId}`, analysisId, id: resultId },
    // Polling is the fallback for a missed event, at the panel's own pace.
    { pollingInterval: isProcessing ? VALUE_CHAIN_MAP_ANALYSIS_RESULTS_POLLING_INTERVAL : 0 },
  );
  const result = data?.data;

  useEffect(() => {
    setIsProcessing(Boolean(result && VALUE_CHAIN_MAP_ANALYSIS_RESULT_PROCESSING_STATUSES.has(result.status)));
  }, [result]);

  const handleResultUpdated = useCallback((event: MessageEvent<string>) => {
    try {
      const payload = JSON.parse(event.data) as IVCAnalysisResultUpdatedPayload;

      if (payload.resultId !== resultId) {
        return;
      }

      dispatch(valueChainMapApi.util.invalidateTags([
        { type: VALUE_CHAIN_MAP_ANALYSES_TAG, id: `${valueChainMapId}-${analysisId}` },
        { type: VALUE_CHAIN_MAP_ANALYSES_TAG, id: `${valueChainMapId}-${analysisId}-result-${resultId}` },
      ]));
    } catch {
      // Ignore malformed socket payloads.
    }
  }, [analysisId, dispatch, resultId, valueChainMapId]);

  useWebSocket({
    event: VALUE_CHAIN_MAP_ANALYSIS_WS_EVENT_RESULT_UPDATED,
    transports: [SOCKET_TRANSPORTS.WEBSOCKET],
    shouldConnect: isProcessing,
    onMessage: handleResultUpdated,
  });

  const isDeleted = isError && isNotFound(error);

  return {
    result: isDeleted ? undefined : result,
    isLoading,
    isDeleted,
    isError: isError && !isDeleted,
  };
};

export default useAiChatAnalysisResult;
